import bech32 from 'bech32'
import crypto from 'crypto'

/**
 * Returns a Bitcoin style address: RIPEMD160(SHA256(pubkey)).
 *
 * @private
 * @param {Buffer} pubkey A public key.
 * @returns {Buffer} A Bitcoin style address.
 */
const hash160 = function (pubkey) {
  const hash = crypto.createHash('sha256').update(pubkey).digest()
  return crypto.createHash('ripemd160').update(hash).digest()
}

/**
 * Returns an encoded cosmos address.
 *
 * @param {string} hrp The human readable part of the address.
 * @param {Buffer} pubkey A public key.
 * @returns {string} A Bitcoin style addresses.
 */
const encodeCosmosAddress = function (hrp, pubkey) {
  const hash = hash160(pubkey)
  return bech32.encode(hrp, bech32.toWords(hash))
}

const stripBOM = function (s) {
  if (typeof s !== 'string') {
    throw new TypeError(`Expected a string, got ${typeof s}`)
  }

  // Catches EFBBBF (UTF-8 BOM) because the buffer-to-string conversion
  // translates it to FEFF (UTF-16 BOM)
  if (s.charCodeAt(0) === 0xFEFF) {
    return s.slice(1)
  }

  return s
}

/**
 * Checking for valid cosmos address.
 *
 * @param {string} address Wallet address with any prefix.
 * @returns {boolean} The checking result.
 */
const isValidCosmosAddress = function (address) {
  return /^cosmos1[ac-hj-np-z02-9]{38}$/.test(address)
}

/**
 * Return address with any prefix into cosmos address.
 *
 * @param {string} address Wallet address with any prefix.
 * @returns {string} A encoded address.
 */
const convertToCosmosAddress = function (address) {
  const { words } = bech32.decode(address)
  return bech32.encode('cosmos', words)
}

/**
 * Return a cosmos address.
 *
 * @param {string} address Wallet address with any prefix.
 * @returns {string} A cosmos address.
 */
const normalizeToCosmosAddress = function (address) {
  return isValidCosmosAddress(address)
    ? address
    : convertToCosmosAddress(address)
}

export {
  hash160,
  encodeCosmosAddress,
  stripBOM,
  normalizeToCosmosAddress
}
